import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, MessageSquare, AlertTriangle, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Partner-side ticket queue — read-only mirror of the admin `/admin/tickets`
// page, scoped server-side to the partner's own cards. No delete/edit; the
// row deep-links to the partner ticket detail page which shows NBN
// credentials + plan + activity.

// Column accessors — pulled out so header clicks and body cells share
// the exact same value extraction. Values are ``.toLowerCase()``d for
// string sort stability; numbers pass through untouched.
const SORT_ACCESSORS = {
  business_name: (r) => (r.business_name || r.contact_name || "").toLowerCase(),
  contact_name:  (r) => (r.contact_name || r.email || "").toLowerCase(),
  stage:         (r) => (r.stage || "").toLowerCase(),
  syncro_ticket: (r) => Number(r.syncro_ticket_number) || 0,
  // Row's most recent edit — falls back to created_at so tickets that
  // haven't been touched still sort in a sensible place instead of
  // sinking to the bottom as `0`.
  updated_at:    (r) => (r.updated_at || r.created_at) ? new Date(r.updated_at || r.created_at).getTime() : 0,
  flags:         (r) => (r.linked_signup_id ? 3 : 0)
                       + (r.plan_mismatch?.is_mismatch && !r.plan_mismatch?.acknowledged ? 2 : 0)
                       + (r.note_count ? 1 : 0),
};

// Compact "5m · 2h · 3d" relative time — inline instead of importing
// date-fns because it's the only relative timestamp on this page.
function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60)     return `${sec}s ago`;
  if (sec < 3600)   return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400)  return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// Persist the user's chosen sort in localStorage so it survives
// refreshes and new sessions. Keyed per-role so partner + admin
// tickets don't cross-contaminate on shared machines.
const SORT_STORAGE_KEY = "partner-tickets-sort";
function loadSort() {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (!raw) return { key: null, dir: "asc" };
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.key === null || SORT_ACCESSORS[parsed.key])
        && (parsed.dir === "asc" || parsed.dir === "desc")) {
      return parsed;
    }
  } catch (_) { /* fall through to default */ }
  return { key: null, dir: "asc" };
}

export default function PartnerTickets() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  // v1.10.45: click any header to sort — first click asc, second desc,
  // third clears. ``key=null`` = default order (server-side).
  // v1.10.113: sort persists to localStorage across sessions.
  const [sort, setSort] = useState(loadSort);
  useEffect(() => {
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sort));
    } catch (_) { /* private browsing / quota — ignore */ }
  }, [sort]);
  function cycleSort(key) {
    setSort((s) => {
      if (s.key !== key)       return { key, dir: "asc" };
      if (s.dir === "asc")     return { key, dir: "desc" };
      return { key: null, dir: "asc" };
    });
  }

  async function load() {
    try {
      const { data } = await api.get("/partner/tickets");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load tickets");
    }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  const stages = useMemo(() => Array.from(new Set((rows || []).map(r => r.stage).filter(Boolean))), [rows]);
  const filtered = useMemo(() => {
    let out = rows || [];
    const term = q.trim().toLowerCase();
    if (term) out = out.filter(r => [r.business_name, r.contact_name, r.email, r.phone, r.service_address, r.syncro_ticket_number].join(" ").toLowerCase().includes(term));
    if (stageFilter) out = out.filter(r => r.stage === stageFilter);
    if (sort.key && SORT_ACCESSORS[sort.key]) {
      const acc = SORT_ACCESSORS[sort.key];
      const dir = sort.dir === "desc" ? -1 : 1;
      out = [...out].sort((a, b) => {
        const av = acc(a); const bv = acc(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return  1 * dir;
        return 0;
      });
    }
    return out;
  }, [rows, q, stageFilter, sort]);

  return (
    <div className="space-y-5" data-testid="partner-tickets-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Tickets</h1>
          <p className="text-sm text-slate-400 mt-1">
            Every one of your onboarding customers in one flat list. Click a row for a full ticket view with contact details, plan info, NBN credentials, and the activity timeline.
          </p>
        </div>
        <div className="text-xs text-slate-500 font-mono">{filtered.length} / {rows?.length || 0}</div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, address" className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 focus:border-cyan-500 outline-none rounded-lg text-sm" data-testid="partner-tickets-search" />
        </div>
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="bg-slate-900 border border-slate-800 rounded-lg text-sm px-3 py-2">
          <option value="">All stages</option>
          {stages.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/50 border-b border-slate-800 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <SortHeader label="Business"  sortKey="business_name"  sort={sort} onSort={cycleSort} />
              <SortHeader label="Contact"   sortKey="contact_name"   sort={sort} onSort={cycleSort} />
              <SortHeader label="Stage"     sortKey="stage"          sort={sort} onSort={cycleSort} />
              <SortHeader label="Ticket #"  sortKey="syncro_ticket"  sort={sort} onSort={cycleSort} />
              <SortHeader label="Updated"   sortKey="updated_at"     sort={sort} onSort={cycleSort} />
              <SortHeader label="Flags"     sortKey="flags"          sort={sort} onSort={cycleSort} />
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No matching tickets.</td></tr>
            ) : filtered.map(r => (
              <tr key={r.id} className="border-b border-slate-800/70 hover:bg-slate-950/60 transition-colors">
                <td className="px-4 py-3">
                  <Link to={`/portal/tickets/${r.id}`} data-testid={`partner-ticket-row-${r.id}`} className="text-slate-100 font-medium hover:text-cyan-300">{r.business_name || r.contact_name || "(no name)"}</Link>
                  <div className="text-[11px] text-slate-500 truncate max-w-xs">{r.service_address}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-slate-200">{r.contact_name || "—"}</div>
                  <div className="text-[11px] text-slate-500 truncate">{r.email} {r.phone && `· ${r.phone}`}</div>
                </td>
                <td className="px-4 py-3"><span className="inline-block text-[10px] uppercase tracking-widest bg-slate-800 text-slate-300 rounded px-1.5 py-0.5">{(r.stage || "").replace(/_/g, " ") || "—"}</span></td>
                <td className="px-4 py-3 text-xs text-slate-400">{r.syncro_ticket_number ? `#${r.syncro_ticket_number}` : "—"}</td>
                <td
                  className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap"
                  data-testid={`partner-ticket-updated-${r.id}`}
                  title={r.updated_at ? new Date(r.updated_at).toLocaleString() : ""}
                >
                  {relativeTime(r.updated_at || r.created_at)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {r.linked_signup_id && <span className="text-[10px] bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 rounded px-1.5 py-0.5 uppercase tracking-widest font-semibold">Signed</span>}
                    {r.plan_mismatch?.is_mismatch && !r.plan_mismatch?.acknowledged && (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-red-500/20 border border-red-500/60 text-red-200 rounded px-1.5 py-0.5 uppercase tracking-widest font-bold"><AlertTriangle className="w-2.5 h-2.5" /> Mismatch</span>
                    )}
                    {r.note_count > 0 && <span className="inline-flex items-center gap-1 text-[10px] bg-slate-800 text-slate-300 rounded px-1.5 py-0.5"><MessageSquare className="w-2.5 h-2.5" /> {r.note_count}</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Sortable table header cell. Click cycles through asc → desc → clear
 * (matching the pattern on the admin customer list). The active
 * column shows a coloured chevron in the current direction; inactive
 * columns show a low-opacity double-chevron hint that they're
 * clickable.
 */
function SortHeader({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ChevronsUpDown : (sort.dir === "asc" ? ChevronUp : ChevronDown);
  return (
    <th className="text-left px-4 py-3 font-semibold">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`partner-tickets-sort-${sortKey}`}
        aria-label={`Sort by ${label}`}
        aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
        className={`inline-flex items-center gap-1.5 uppercase tracking-widest transition-colors ${active ? "text-cyan-300" : "hover:text-cyan-300"}`}
      >
        {label}
        <Icon className={`w-3 h-3 ${active ? "text-cyan-300" : "opacity-40"}`} />
      </button>
    </th>
  );
}
